/**
 * 提示词服务 - API接口封装
 */

import { API_BASE_URL } from '../config/api';

// 获取认证令牌的辅助函数
function getAuthToken(): string | null {
  return localStorage.getItem('auth_token');
}

const API_BASE = `${API_BASE_URL}/api/v1`;

// 类型定义
export interface Prompt {
  id: string;
  title: string;
  content: string;
  description: string;
  tags: string[];
  category: string;
  userId: string;
  createdAt: number;
  updatedAt: number;
  usageCount: number; // 复制使用次数
  viewCount: number;  // 查看详情次数
  favoriteCount: number; // 收藏数量（为未来提示词广场准备）
  isFavorite: boolean;
  deletedAt?: number; // 删除时间（回收站功能）
  isDeleted?: boolean; // 是否已删除
}

export interface PromptInput {
  title?: string;
  content: string;
  description?: string;
  tags?: string;
  category?: string;
  isFavorite?: boolean;
}

export interface PromptUpdate {
  title?: string;
  content?: string;
  description?: string;
  tags?: string;
  category?: string;
  isFavorite?: boolean;
}

export interface PromptListQuery {
  page?: number;
  limit?: number;
  search?: string;
  category?: string;
  tags?: string[];
  sortBy?: 'createdAt' | 'updatedAt' | 'title' | 'usageCount' | 'viewCount';
  sortOrder?: 'asc' | 'desc';
  onlyDeleted?: boolean; // 只获取已删除的提示词（回收站功能）
}

export interface PromptListResponse {
  prompts: Prompt[];
  total: number;
  page: number;
  limit: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
}

export interface PromptStats {
  totalPrompts: number;
  totalCategories: number;
  totalTags: number;
  recentPrompts: number;
  favoritePrompts: number;
}

interface APIResponse<T = any> {
  success: boolean;
  data?: T;
  error?: {
    code: string;
    message: string;
    details?: any;
  };
  timestamp: number;
}

class PromptAPIError extends Error {
  code: string;
  details?: any;

  constructor(code: string, message: string, details?: any) {
    super(message);
    this.name = 'PromptAPIError';
    this.code = code;
    this.details = details;
  }
}

/**
 * 通用API请求方法
 */
async function apiRequest<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const token = getAuthToken();
  if (!token) {
    throw new PromptAPIError('UNAUTHORIZED', '请先登录');
  }

  const url = `${API_BASE}${endpoint}`;
  const config: RequestInit = {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      ...options.headers,
    },
  };

  try {
    const response = await fetch(url, config);
    const data: APIResponse<T> = await response.json();

    // 检查是否是认证相关错误（401状态码）
    if (response.status === 401) {
      const errorCode = data.error?.code;
      
      // 处理账号被禁用或认证失败的情况
      if (errorCode === 'ACCOUNT_DISABLED' || 
          errorCode === 'INVALID_TOKEN' || 
          errorCode === 'USER_NOT_FOUND') {
        // 清除本地存储的认证信息
        localStorage.removeItem('auth_token');
        localStorage.removeItem('user');
        
        // 重定向到登录页面
        window.location.href = '/auth/login';
        
        // 显示错误消息
        if (errorCode === 'ACCOUNT_DISABLED') {
          alert('您的账号已被禁用，请联系管理员');
        } else {
          alert('登录已过期，请重新登录');
        }
        
        throw new PromptAPIError(errorCode, data.error?.message || '认证失败');
      }
    }

    if (!data.success) {
      throw new PromptAPIError(
        data.error?.code || 'API_ERROR',
        data.error?.message || '请求失败',
        data.error?.details
      );
    }

    return data.data!;
  } catch (error) {
    if (error instanceof PromptAPIError) {
      throw error;
    }

    console.error('API请求失败:', error);
    throw new PromptAPIError('NETWORK_ERROR', '网络请求失败，请检查网络连接');
  }
}

/**
 * 提示词服务类
 */
export class PromptService {
  /**
   * 创建提示词
   */
  static async createPrompt(input: PromptInput): Promise<{ message: string; prompt: Prompt }> {
    return apiRequest('/me/prompts', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  }

  /**
   * 获取提示词列表
   */
  static async getPrompts(query: PromptListQuery = {}): Promise<PromptListResponse & { message: string }> {
    const params = new URLSearchParams();
    
    Object.entries(query).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        if (Array.isArray(value)) {
          params.append(key, value.join(','));
        } else {
          params.append(key, String(value));
        }
      }
    });

    const queryString = params.toString();
    const endpoint = `/me/prompts${queryString ? `?${queryString}` : ''}`;
    
    return apiRequest(endpoint);
  }

  /**
   * 获取单个提示词
   */
  static async getPrompt(id: string): Promise<{ message: string; prompt: Prompt }> {
    return apiRequest(`/me/prompts/${id}`);
  }

  /**
   * 更新提示词
   */
  static async updatePrompt(id: string, updates: PromptUpdate): Promise<{ message: string; prompt: Prompt }> {
    return apiRequest(`/me/prompts/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  /**
   * 删除提示词
   */
  static async deletePrompt(id: string): Promise<{ message: string }> {
    return apiRequest(`/me/prompts/${id}`, {
      method: 'DELETE',
    });
  }

  /**
   * 增加提示词使用次数
   */
  static async usePrompt(id: string): Promise<{ message: string }> {
    return apiRequest(`/me/prompts/${id}/use`, {
      method: 'POST',
    });
  }

  /**
   * 查看提示词详情（增加查看次数）
   */
  static async viewPrompt(id: string): Promise<{ message: string }> {
    return apiRequest(`/me/prompts/${id}/view`, {
      method: 'POST',
    });
  }

  /**
   * 获取用户统计信息
   */
  static async getStats(): Promise<{ message: string; stats: PromptStats }> {
    return apiRequest('/me/prompts/stats');
  }

  /**
   * 切换收藏状态
   */
  static async toggleFavorite(id: string, isFavorite: boolean): Promise<{ message: string; prompt: Prompt }> {
    return this.updatePrompt(id, { isFavorite });
  }

  /**
   * 复制提示词内容到剪贴板
   */
  static async copyToClipboard(content: string): Promise<boolean> {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(content);
      } else {
        // 降级处理：使用传统方法
        const textArea = document.createElement('textarea');
        textArea.value = content;
        textArea.style.position = 'absolute';
        textArea.style.left = '-999999px';
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
      return true;
    } catch (error) {
      console.error('复制失败:', error);
      return false;
    }
  }

  /**
   * 格式化标签字符串为数组
   */
  static formatTagsToArray(tags: string | string[]): string[] {
    if (Array.isArray(tags)) return tags;
    if (!tags) return [];
    return tags.split(',').map(tag => tag.trim()).filter(tag => tag.length > 0);
  }

  /**
   * 格式化标签数组为字符串
   */
  static formatTagsToString(tags: string[]): string {
    return tags.join(', ');
  }

  /**
   * 生成提示词摘要
   */
  static generateSummary(content: string, maxLength: number = 100): string {
    const text = content.replace(/\n/g, ' ').trim();
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  }

  /**
   * 验证提示词输入
   */
  static validatePromptInput(input: PromptInput): { valid: boolean; errors: string[] } {
    const errors: string[] = [];

    if (!input.content || input.content.trim().length === 0) {
      errors.push('提示词内容不能为空');
    }

    if (input.content && input.content.length > 10000) {
      errors.push('提示词内容不能超过10000个字符');
    }

    if (input.title && input.title.length > 200) {
      errors.push('标题不能超过200个字符');
    }

    if (input.description && input.description.length > 1000) {
      errors.push('描述不能超过1000个字符');
    }

    if (input.category && input.category.length > 100) {
      errors.push('分类不能超过100个字符');
    }

    return { valid: errors.length === 0, errors };
  }

  // ==================== 回收站相关方法 ====================

  /**
   * 获取回收站中的提示词
   */
  static async getTrashPrompts(query: PromptListQuery = {}): Promise<PromptListResponse & { message: string }> {
    const params = new URLSearchParams();
    
    // 添加 onlyDeleted 参数
    params.append('onlyDeleted', 'true');
    
    Object.entries(query).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        if (Array.isArray(value)) {
          params.append(key, value.join(','));
        } else {
          params.append(key, String(value));
        }
      }
    });

    const queryString = params.toString();
    const endpoint = `/me/prompts${queryString ? `?${queryString}` : ''}`;
    
    return apiRequest(endpoint);
  }

  /**
   * 恢复提示词（从回收站恢复）
   */
  static async restorePrompt(id: string): Promise<{ message: string }> {
    return apiRequest(`/me/prompts/${id}/restore`, {
      method: 'POST',
    });
  }

  /**
   * 永久删除提示词
   */
  static async permanentDeletePrompt(id: string): Promise<{ message: string }> {
    return apiRequest(`/me/prompts/${id}/permanent`, {
      method: 'DELETE',
    });
  }

  /**
   * 清空回收站
   */
  static async emptyTrash(): Promise<{ message: string; deleted: number }> {
    return apiRequest('/me/prompts/trash', {
      method: 'DELETE',
    });
  }

  /**
   * 自动清理过期的回收站项目
   */
  static async autoCleanupTrash(): Promise<{ message: string; deleted: number }> {
    return apiRequest('/me/prompts/cleanup-trash', {
      method: 'POST',
    });
  }

  /**
   * 批量删除提示词（移入回收站）
   */
  static async batchDeletePrompts(promptIds: string[]): Promise<{ message: string; success: number; failed: number }> {
    return apiRequest('/me/prompts/batch-delete', {
      method: 'POST',
      body: JSON.stringify({ promptIds }),
    });
  }
}

export { PromptAPIError }; 